/**
 * useSessionContact - session-only memory of the user's contact + state
 * details so they don't have to re-type name/email/phone/state/suburb
 * when they run a second (or third) MiniMatcher inside the same tab.
 *
 * Why sessionStorage (NOT localStorage):
 *   - Clears automatically when the browser tab closes
 *   - Never shared across tabs / windows / devices
 *   - PII is held just long enough to be convenient, not long enough to
 *     accumulate a profile, which keeps the privacy story simple:
 *     "we hold your details in your own browser tab while you're using
 *     BuildPilot, and forget them as soon as you leave."
 *
 * Storage shape (key `bp.sessionContact`):
 *   { name, email, countryCode, phone, state, suburb, updatedAt }
 *
 * Anything else (priorities, build_type, budget, …) stays in
 * `useAnonymousBrief`, which is the no-PII project brief.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'bp.sessionContact';

const _empty = () => ({
  name: '',
  email: '',
  countryCode: '+61',
  phone: '',
  state: '',
  suburb: '',
  updatedAt: null,
});

const _read = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return _empty();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return _empty();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return _empty();
    return { ..._empty(), ...parsed };
  } catch {
    return _empty();
  }
};

const _write = (contact) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(contact));
  } catch { /* ignore quota errors */ }
};

export function useSessionContact() {
  const [contact, setContact] = useState(_read);

  // Persist on every change so a second MiniMatcher (opened later in
  // the same tab) reads the freshest values.
  useEffect(() => {
    _write(contact);
  }, [contact]);

  /** Shallow-merge a partial update. Empty / null values are ignored
   *  so we never overwrite a known good value with a blank field. */
  const update = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    setContact((prev) => {
      const next = { ...prev };
      Object.entries(partial).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        next[k] = v;
      });
      next.updatedAt = new Date().toISOString();
      return next;
    });
  }, []);

  /** Wipe everything (e.g. user explicitly clicks "Not me"). */
  const clear = useCallback(() => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
    setContact(_empty());
  }, []);

  /** True once the user has provided enough to skip re-typing. */
  const hasContact = Boolean(contact?.name && contact?.email);

  return { contact, update, clear, hasContact };
}

export default useSessionContact;
